// Stage 38a — WebSocket client for live notification push.
//
// Connection lifecycle:
//   1. connect() opens new WebSocket(url). No token in URL — the
//      server-side first-message handshake is the auth surface.
//   2. On open: send {"type":"auth","token":<jwt>} as first frame.
//   3. On message: parse JSON; dispatch CustomEvent('cos:notification',
//      {detail: parsed.notification}) on window. The cos-notification-
//      bell listens for it.
//   4. On close/error: reconnect with exponential backoff (1s, 2s,
//      4s, 8s, capped at 30s). The backoff resets on successful
//      auth_ok.
//
// Visibility-API: paused reconnects while document.hidden so a
// backgrounded tab doesn't burn CPU + battery hammering a
// disconnected socket. Resumes on visibilitychange.
//
// Disconnect on cos:auth:expired (the logout signal) so a logged-
// out tab doesn't keep an authenticated socket alive.

const BACKOFF_INITIAL_MS = 1000;
/**
 * The server's close code for "your token is genuine but COS cannot
 * resolve it to a user it can address" (RFC 6455 application range).
 * Distinct from an auth failure precisely because retrying cannot fix it.
 * Must match wsCloseUnresolvedIdentity in backend/internal/handler/websocket.go.
 */
const WS_CLOSE_UNRESOLVED_IDENTITY = 4003;

const BACKOFF_MAX_MS = 30_000;

export type NotificationsWSEventMap = {
	'cos:notification': CustomEvent<unknown>;
};

export class NotificationsWS {
	private ws: WebSocket | null = null;
	private url: string;
	private getToken: () => string | null;
	private backoffMs = BACKOFF_INITIAL_MS;
	private reconnectTimer: number | null = null;
	private intentionalClose = false;
	private hidden = false;

	constructor(url: string, getToken: () => string | null) {
		this.url = url;
		this.getToken = getToken;
		// Bind handlers once so addEventListener/removeEventListener
		// can pair them.
		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', this._onVisibility);
		}
		if (typeof window !== 'undefined') {
			window.addEventListener('cos:auth:expired', this._onAuthExpired);
		}
	}

	connect(): void {
		this.intentionalClose = false;
		if (this.hidden) return; // visibilitychange will trigger when tab returns
		this._open();
	}

	disconnect(): void {
		this.intentionalClose = true;
		if (this.reconnectTimer !== null) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.ws) {
			try { this.ws.close(); } catch { /* noop */ }
			this.ws = null;
		}
	}

	private _open(): void {
		const token = this.getToken();
		if (!token) {
			// 65a-4 fix (06-12): the token can arrive AFTER first mount
			// (refresh-cookie session restore is async). Returning without
			// scheduling anything left the socket permanently dead until a
			// visibilitychange — live chat silently became reload-to-see.
			// Retry on the normal backoff until the token shows up.
			this._scheduleReconnect();
			return;
		}
		let ws: WebSocket;
		try {
			ws = new WebSocket(this.url);
		} catch {
			this._scheduleReconnect();
			return;
		}
		this.ws = ws;
		ws.addEventListener('open', () => {
			// First-message auth handshake per Stage 38a design.
			try {
				ws.send(JSON.stringify({ type: 'auth', token }));
			} catch {
				/* will be retried on next reconnect */
			}
		});
		ws.addEventListener('message', (ev) => {
			let parsed: any;
			try { parsed = JSON.parse(ev.data); } catch { return; }
			if (parsed && parsed.type === 'auth_ok') {
				// Reset backoff on successful auth — a long stable
				// connection followed by a transient drop should
				// reconnect quickly, not at the previous capped
				// backoff value.
				this.backoffMs = BACKOFF_INITIAL_MS;
				return;
			}
			if (parsed && parsed.type === 'notification' && parsed.notification) {
				const event = new CustomEvent('cos:notification', {
					detail: parsed.notification,
				});
				if (typeof window !== 'undefined') {
					window.dispatchEvent(event);
				}
			}
			// A finished report PREVIEW. Transient by design: the page
			// waiting on it needs the signal, but a preview is not an
			// artifact anyone asked to keep, so it never becomes a
			// notification (no bell row, no history entry).
			if (parsed && parsed.type === 'report_preview_ready' && parsed.payload) {
				if (typeof window !== 'undefined') {
					window.dispatchEvent(
						new CustomEvent('cos:report-preview-ready', { detail: parsed.payload }),
					);
				}
			}
			// Stage 65a-2 — live chat frames ride the same socket.
			if (parsed && parsed.type === 'chat_message' && parsed.message) {
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('cos:chat-message', { detail: parsed.message }));
				}
			}
			// 65a-3b — read-cursor advances (seen-by receipts).
			if (parsed && parsed.type === 'chat_read' && parsed.read) {
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('cos:chat-read', { detail: parsed.read }));
				}
			}
			// 65a-3 — reaction toggles.
			if (parsed && parsed.type === 'chat_reaction' && parsed.reaction) {
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('cos:chat-reaction', { detail: parsed.reaction }));
				}
			}
			// 65a-5 — pin toggles.
			if (parsed && parsed.type === 'chat_pin' && parsed.pin) {
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('cos:chat-pin', { detail: parsed.pin }));
				}
			}
			// 65a-4 — platform-wide status changes.
			if (parsed && parsed.type === 'chat_status' && parsed.status) {
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('cos:chat-status', { detail: parsed.status }));
				}
			}
			// Other frame types (auth_error, future ping) silently
			// ignored at this layer; logging would spam in tests.
		});
		ws.addEventListener('close', (ev) => {
			this.ws = null;
			if (this.intentionalClose) return;
			// A refusal reconnecting cannot fix. Backing off forever against
			// it is the exact silence that let live delivery be broken for
			// everybody without anyone noticing: the bell and the chat panes
			// both hydrate over REST on mount, so the page looks fine and
			// only the LIVE half is missing. Stop, and say so.
			if (ev.code === WS_CLOSE_UNRESOLVED_IDENTITY) {
				this.intentionalClose = true;
				if (typeof window !== 'undefined') {
					window.dispatchEvent(new CustomEvent('cos:notifications-unavailable', {
						detail: { reason: 'unresolved-identity' },
					}));
				}
				return;
			}
			this._scheduleReconnect();
		});
		ws.addEventListener('error', () => {
			// 'close' fires after 'error'; let close handle the
			// reconnect schedule.
		});
	}

	private _scheduleReconnect(): void {
		if (this.intentionalClose || this.hidden) return;
		if (this.reconnectTimer !== null) return; // already scheduled
		const delay = this.backoffMs;
		this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
		this.reconnectTimer = window.setTimeout(() => {
			this.reconnectTimer = null;
			this._open();
		}, delay);
	}

	private _onVisibility = () => {
		this.hidden = document.hidden;
		if (this.hidden) {
			// Pause reconnect attempts while hidden — let any
			// existing socket time out naturally.
			if (this.reconnectTimer !== null) {
				clearTimeout(this.reconnectTimer);
				this.reconnectTimer = null;
			}
			return;
		}
		// Tab returned — if we don't have a connection, reconnect
		// immediately (don't make the user wait for backoff).
		if (!this.ws && !this.intentionalClose) {
			this.backoffMs = BACKOFF_INITIAL_MS;
			this._open();
		}
	};

	private _onAuthExpired = () => {
		this.disconnect();
	};

	// Test-helper exposure: returns the current backoff value so
	// unit tests can assert the exponential ramp.
	currentBackoffMs(): number { return this.backoffMs; }
}
